import { Component, OnInit, Optional, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { RolesService } from '../../../../core/services/user/roles.service';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { ToastService } from '../../../../core/services/toast.service';
import { UserService } from '../../../../core/services/user/user.service';
import { OrganizationService } from 'app/core/services/organization/organization.service';
import { Organization } from 'app/core/models/organization/organization.model';
import { FacilityService } from 'app/core/services/organization/facility.service';
import { GenderUtils } from 'app/utils/gender-utils';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { EmailValidatorService } from '../../../validators/email.validator';

function noOldDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const inputDate = new Date(control.value);
  const minDate = new Date('1900-01-01');

  if (inputDate < minDate) {
    return { oldDate: true };
  }

  return null;
}

@Component({
  selector: 'app-create-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NgxIntlTelInputModule,
    PhoneInputDirective,
    SelectComponent,
  ],
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss',
})
export class CreateUserComponent implements OnInit {
  @Input() data: any;
  userForm: FormGroup;
  roles: any = [];
  organizationList: any = [];
  organizationFacilitiesList: any = [];
  selectedFacilities: any;
  maxDate: string;
  currentUser: UserSessionModel | null = null;
  isOverlayLoading: boolean = false;
  phoneNumberPattern = '^[0-9]*$';
  organizations: Organization[] = [];
  isLoadingOrganizations = false;
  facilities: any[] = [];
  isLoadingFacilities = false;
  genders: Array<{ id: number; name: string }> = [];
  defaultCountry: CountryISO = CountryISO.Turkey;
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  isFacilityAdmin: boolean = false;

  constructor(
    private router: Router,
    private rolesService: RolesService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private toastService: ToastService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private facilityService: FacilityService,
    private emailValidatorService: EmailValidatorService,
    @Optional() public activeModal?: NgbActiveModal,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    this.userForm = this.fb.group({
      roleId: [{ value: null, disabled: true }, Validators.required],
      organizationId: [null, Validators.required],
      facilityId: [{ value: null, disabled: true }, Validators.required],
      countryCode: [''],
      gender: [null, Validators.required],
      name: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      surname: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      pid: [null],
      dateOfBirth: [null, [Validators.required, noOldDateValidator]],
      phoneNumber: [null, [Validators.required]],
      email: [
        null,
        [Validators.required, this.emailValidatorService.validEmailFormat(), Validators.maxLength(50)],
      ],
    });
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();

    this.isFacilityAdmin = this.currentUserService.hasClaim(
      'facility_admin_claim',
    );

    const userOrgId = this.currentUser?.userOrganizations?.[0]?.organizationId;

    if (!userOrgId) {
      this.getOrganizations();
    }

    this.userForm.get('organizationId')?.valueChanges.subscribe((orgId) => {
      if (orgId) {
        if (!this.isFacilityAdmin) {
          this.loadFacilities(orgId);
          this.userForm.patchValue({ facilityId: null, roleId: null });
          this.userForm.get('facilityId')?.enable();
        }
        this.userForm.get('roleId')?.disable();

        if (this.roles.length > 0) {
          this.roles = [];
        }
        this.getRoles(orgId);
      } else {
        this.facilities = [];
        this.userForm.patchValue({ facilityId: null, roleId: null });
        this.userForm.get('facilityId')?.disable();
        this.userForm.get('roleId')?.disable();
      }
    });

    if (userOrgId) {
      this.userForm.patchValue({ organizationId: userOrgId });

      if (this.isFacilityAdmin) {
        const userFacilityId = this.currentUserService.getFacilityId();
        if (userFacilityId) {
          this.userForm.patchValue({ facilityId: userFacilityId });
          this.userForm.get('roleId')?.enable();
        }
      }
    }

    this.userForm.get('facilityId')?.valueChanges.subscribe((facId) => {
      if (facId) {
        this.userForm.get('roleId')?.enable();
      } else {
        this.userForm.patchValue({ roleId: null });
        this.userForm.get('roleId')?.disable();
      }
    });

    GenderUtils.initializeTranslateService(this.translate);
    this.genders = GenderUtils.getAllGenders();
  }

  preventForbiddenCharacters(
    event: Event,
    type: 'number' | 'text' | 'email',
  ): void {
    const inputElement = event.target as HTMLInputElement;
    let val: string | number = inputElement.value;

    if (type === 'number') {
      val = Number(inputElement.value);
      if (isNaN(val) || val < 1) {
        inputElement.value = '';
        return;
      }
    } else if (type === 'text') {
      if (/^[\s]|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    } else if (type === 'email') {
      if (/^[\s]|[^a-zA-Z0-9@._]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-Z0-9@._]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    }
  }

  getRoles(organizationId: string | null = null) {
    this.rolesService.getRoles(0, 1000, organizationId).subscribe({
      next: (roles) => {
        this.roles = (roles?.data?.items ?? []).map((x: any) => ({
          id: x.role?.id,
          name: x.role?.name,
        }));
      },
      error: (err) => {
        console.error('getRoles error:', err);
        this.toastService.showToast(
          'Role listesi yüklenirken hata oluştu.',
          'error',
        );
      },
    });
  }

  getOrganizations() {
    this.isLoadingOrganizations = true;

    this.organizationService.getAllOrganizationItems().subscribe({
      next: (items) => {
        this.organizations = items;
        this.isLoadingOrganizations = false;
      },
      error: () => {
        this.isLoadingOrganizations = false;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }

  get formDebugInfo() {
    if (!this.userForm) return [];

    return Object.keys(this.userForm.controls).map((key) => {
      const control = this.userForm.get(key);
      return {
        name: key,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
        value: control?.value,
      };
    });
  }

  // SADECE CREATE
  saveUser(e: Event) {
    e.preventDefault();

    let facilityName = '';
    let organizationName = '';
    let facilityId = this.userForm.value.facilityId;
    let organizationId = this.userForm.value.organizationId;

    if (this.isFacilityAdmin && this.currentUser?.userOrganizations?.length) {
      const userOrg = this.currentUser.userOrganizations[0];
      organizationId = userOrg.organizationId;
      organizationName = userOrg.organizationName;

      if (userOrg.userFacilities?.length) {
        const userFac = userOrg.userFacilities[0];
        facilityId = userFac.facilityId;
        facilityName = userFac.facilityName;
      }
    } else {
      const facility = this.facilities.find(
        (x) => x.id === this.userForm.value.facilityId,
      );
      if (facility) {
        facilityName = facility.name;
      }

      const organization = this.organizations.find(
        (x) => x.id === this.userForm.value.organizationId,
      );
      if (organization) {
        organizationName = organization.name;
      } else if (this.currentUser?.userOrganizations) {
        const userOrg = this.currentUser.userOrganizations.find(
          (u) => u.organizationId === this.userForm.value.organizationId,
        );
        organizationName = userOrg?.organizationName || '';
      }
    }

    const phoneControl = this.userForm.get('phoneNumber');

    const phoneValue: any = phoneControl?.value;
    const phoneObj = this.userForm.value.phoneNumber;
    const cleanedNumber = phoneObj.number.replace(/\D/g, '');

    this.isOverlayLoading = true;

    var formData = {
      countryCode: phoneValue?.dialCode ?? null,
      firstName: this.userForm.value.name,
      lastName: this.userForm.value.surname,
      email: this.userForm.value.email,
      phone: cleanedNumber,
      pid: this.userForm.value.pid,
      roleId: this.userForm.value.roleId,
      organizationId: organizationId,
      birthDate: this.formatDate(this.userForm.value.dateOfBirth),
      facilityId: facilityId,
      facilityName: facilityName,
      customerCode: this.currentUser?.customerCode,
      gender: this.userForm.value.gender,
      UserOrganizationFacility: {
        OrganizationId: organizationId,
        OrganizationName: organizationName,
        Facilities: [
          {
            FacilityId: facilityId,
            FacilityName: facilityName,
          },
        ],
      },
    };

    console.log('Form Bilgi', formData);

    this.userService.createUser(formData).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successCreateUserDesc'),
          'success',
        );

        if (this.activeModal) {
          this.activeModal.close('success');
        } else {
          setTimeout(() => {
            this.router.navigate(['/user/list']);
          }, 1000);
        }
      },
      error: () => {
        this.isOverlayLoading = false;
      },
      complete: () => {
        this.isOverlayLoading = false;
      },
    });
  }
  onCancel(): void {
    if (this.activeModal) {
      this.activeModal.dismiss('cancel');
      return;
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return control
      ? control.invalid && (control.touched || control.dirty)
      : false;
  }

  onChangeSelectedFacilities(event: any) {
    const selectedFacilitiesId = (event.target as HTMLSelectElement).value;
    this.selectedFacilities = this.organizationFacilitiesList.find(
      (item: any) => item.id == selectedFacilitiesId,
    );
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  loadFacilities(organizationId: string) {
    if (!organizationId) {
      this.facilities = [];
      this.userForm.patchValue({ facilityId: null });
      return;
    }

    this.isLoadingFacilities = true;

    this.facilityService
      .getAllFacilitiesByOrganization(organizationId)
      .subscribe({
        next: (res: any) => {
          this.facilities = res?.data ?? [];
          this.isLoadingFacilities = false;
        },
        error: () => {
          this.isLoadingFacilities = false;
        },
      });
  }
}
